/**
 * What "Reuse" loads into the Broadcast composer. One shape for both sources (a sent broadcast from History, and a reminder Reschedule
 * cancelled because it MAY have been sent), so BroadcastsTab has ONE state path that prefills the composer: nothing is sent, the host still
 * dry-runs and confirms.
 */
import { audienceForReuse, audienceForScheduledReuse } from '../components/groups/broadcasts/audience';
import type { GroupBroadcastSummary, GroupScheduledBroadcast } from '../types/groups';

export interface ComposerDraft {
  subject: string;
  body: string;
  /** Restored WITH the channels: restoring one without the other re-sends an email+SMS broadcast as email only, or re-arms SMS with the wrong text. */
  smsBody: string;
  channels: string[];
  /** An `AUDIENCES` value, or `AUDIENCE_UNCHOSEN` when the source cannot prove who it went to. */
  audience: string;
  /**
   * Personalised invite links change the RENDERED text (each guest gets their own token), and the send's idempotency key hashes the rendered message, so a
   * Reuse with a different setting is a NEW key and guests who already got the original get it again. A scheduled row records it, so it is restored; sent
   * history does not store it (undefined: the composer goes back to the group's default rather than keeping whatever the previous send left in it).
   */
  personalizeInviteLinks?: boolean;
}

/** A sent broadcast: its audience is restored only when history can prove it (see `audienceForReuse`). */
export function draftFromBroadcast(past: GroupBroadcastSummary): ComposerDraft {
  return {
    subject: past.subject,
    body: past.body,
    smsBody: past.smsBody || '',
    channels: past.channels ?? [],
    audience: audienceForReuse(past.segment),
    personalizeInviteLinks: undefined,           // not stored with a sent broadcast
  };
}

/** A scheduled reminder (in practice one cancelled as in doubt): subject, body, text and channels as written; the audience per `audienceForScheduledReuse`. */
export function draftFromScheduled(
  row: Pick<GroupScheduledBroadcast, 'subject' | 'body' | 'smsBody' | 'channels' | 'audienceKind' | 'segment' | 'inviteeIds' | 'personalizeInviteLinks'>,
): ComposerDraft {
  return {
    subject: row.subject,
    body: row.body,
    smsBody: row.smsBody || '',
    channels: row.channels ?? [],
    audience: audienceForScheduledReuse(row),
    personalizeInviteLinks: Boolean(row.personalizeInviteLinks),
  };
}
